import { createContext, useContext, useMemo, useState } from 'react';

import type { ModalKey } from '@/modals/keys';
import { modalRegistry } from '@/modals/registry';

import { VNBaseModal } from './VNBaseModal';

export type ModalConfig = {
  title: React.ReactNode;
  size: 'sm' | 'md' | 'lg' | 'xl';
  node: React.ReactNode;
  id?: string | null;
  fullscreen?: boolean;
  cssClassName?: string;
};

const defaultModal: ModalConfig = {
  title: 'modal',
  size: 'sm',
  node: null,
  id: null,
  fullscreen: false,
  cssClassName: null,
};

interface ModalContextProps {
  onOpen: (
    title: string,
    size: string,
    node: React.ReactNode,
    id?: string,
    fullscreen?: boolean,
    options?: {
      id?: string;
    }
  ) => void;
  onClose: () => void;
  updateTitle: (title: React.ReactNode) => void;
  updateModalSize: (size: 'sm' | 'md' | 'lg' | 'xl') => void;
  openByKey: (key: ModalKey, props?: any) => void;
  // registerActions: (actions: ModalActionConfig) => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalContext.Provider');
  }
  return context;
};

export const VNModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [openModal, setOpenModal] = useState(false);
  const [modalArgs, setModalArgs] = useState<ModalConfig>(defaultModal);
  const values = useMemo(
    () => ({
      onOpen: (title: string, size: 'sm' | 'md' | 'lg' | 'xl', node: React.ReactNode, id: string, options = {}) => {
        setModalArgs({
          title,
          size,
          node,
          id,
        });
        setOpenModal(true);
      },
      onClose: () => {
        setOpenModal(false);
        setModalArgs(defaultModal);
      },
      updateTitle: (title: React.ReactNode) => {
        setModalArgs((prev) => ({
          ...prev,
          title,
        }));
      },
      updateModalSize: (size: 'sm' | 'md' | 'lg' | 'xl') => {
        setModalArgs((prev) => ({
          ...prev,
          size,
        }));
      },
      openByKey: (key: ModalKey, props?: any) => {
        const factory = modalRegistry[key];
        if (!factory) {
          console.error(`Modal not registered: ${key}`);
          return;
        }

        const config = factory(props);

        if (!config || !config.node) {
          console.error(`Modal config invalid for key: ${key}`, config);
          return;
        }

        // console.log({ config });

        setModalArgs({
          title: config.title,
          size: config.size ?? 'sm',
          node: config.node,
          id: config.id ?? null,
          fullscreen: config.fullscreen ?? false,
          cssClassName: config.cssClassName ?? '',
        });

        setOpenModal(true);
      },
      // registerActions: (actions: ModalActionConfig) => {
      //   setModalArgs((prev) => ({ ...prev, actions }));
      // },
    }),
    []
  );

  return (
    <ModalContext.Provider value={values}>
      {children}

      {openModal && (
        <VNBaseModal
          open={openModal}
          title={modalArgs.title as string}
          width={modalArgs.size}
          onClose={values.onClose}
          cssClassName={modalArgs.cssClassName}
          id={modalArgs.id}
        >
          {modalArgs.node}
        </VNBaseModal>
      )}
    </ModalContext.Provider>
  );
};
